import { ChangeDetectionStrategy, Component, ComponentRef, OnInit, computed, effect, inject, signal, untracked } from "@angular/core";

import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter, take } from "rxjs";

import {
  ActiveRound,
  AlertBadge,
  BetEvent,
  BetEventType,
  CancelBetResponse,
  FreeBet,
  HttpError,
  HttpErrorCode,
  OperationType,
  PromoType,
  RoundCancelReason,
  RoundPlay,
  TournamentStatus,
} from "@models";
import { TranslatePipe } from "@pipes";
import { ActionService, AlertService, AnalyticsService, CurrencyService, GameService } from "@services";
import { Calculator, Utils } from "@utils";

import { AbstractStoppableComponent } from "../abstract/abstract-stoppable.component";
import { AmountComponent } from "../amount/amount.component";
import { ButtonComponent } from "../button/button.component";
import { CalculatorModalComponent } from "../calculator-modal/calculator-modal.component";
import { MaxWinModalComponent } from "../max-win-modal/max-win-modal.component";
import { MinesPanelComponent } from "../mines-panel/mines-panel.component";
import { ModalService } from "../modal/modal.service";
import { SvgComponent } from "../svg/svg.component";

enum ButtonState {
  S = "start",
  P_CA = "pick_cancel",
  P_CO = "pick_cash_out",
  A = "autoplay",
}

@UntilDestroy()
@Component({
  selector: "mn-bet-panel",
  templateUrl: "./bet-panel.component.html",
  styleUrls: ["./bet-panel.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AmountComponent, ButtonComponent, MinesPanelComponent, TranslatePipe, SvgComponent],
  host: { "(window:keydown.space)": "onSpace()" },
})
export class BetPanelComponent extends AbstractStoppableComponent implements OnInit {
  private readonly crS = inject(CurrencyService);
  private readonly anS = inject(AnalyticsService);
  private readonly gS = inject(GameService);
  private readonly acS = inject(ActionService);
  private readonly mS = inject(ModalService);
  private readonly alS = inject(AlertService);

  protected readonly prefix = this.crS.symbol;
  protected readonly precision = this.crS.precision;
  protected readonly code = this.crS.code;

  protected readonly B_S = ButtonState;

  /*Bet amount*/
  protected readonly am = signal<string>("0");

  /*Min Amount*/
  protected readonly minA = this.acS.cSB$()?.min_bet!;
  /*Max Bet Amount*/
  protected readonly maxA = this.acS.cSB$()?.max_bet!;
  /*Default Bet*/
  private readonly defB = this.acS.cSB$()?.default_bet!;

  private aS = false;
  private currA = "0";
  private pending: BetEvent | null = null;
  private sTimeout: ReturnType<typeof setTimeout> | null = null;

  /*Active Round*/
  protected readonly aR = computed<ActiveRound | null>(() => this.acS.aR$());
  /*Waiting Round*/
  protected readonly wR = computed<string>(() => this.acS.wR$());
  /*Action in flight or loading*/
  protected readonly ac = computed<boolean>(() => {
    if (this.acS.lR$()) {
      return true;
    }

    if (this.wR()) {
      return true;
    }

    return this.acS.aP$();
  });
  /*Button Disabled*/
  protected readonly bDi = computed<boolean>(() => {
    return this.ac();
    /*todo check*/
  });
  /*Amount Visible*/
  protected readonly aVi = computed<boolean>(() => {
    if (this.aR()) {
      return false;
    }

    return this.st() === ButtonState.S;
  });
  /*State*/
  protected readonly st = computed<ButtonState>(() => {
    if (this.acS.wP$()) {
      return !this.aR() ? ButtonState.P_CA : ButtonState.P_CO;
    }

    return ButtonState.S;
  });
  /*Promo type*/
  protected readonly po = computed<PromoType | null>(() => {
    if (this.fB()) {
      return PromoType.F_B;
    }

    if (this.to()) {
      return PromoType.T;
    }

    return null;
  });
  /*Collect button win display*/
  protected readonly totalWin = computed<string>(() => {
    const active = this.acS.aR$();
    if (!active) {
      return "";
    }

    if (Calculator.eq(active.total_win, "0")) {
      return "";
    }

    return `${this.prefix}${this.crS.round({ value: active.total_win, fallback: "0" })}`;
  });

  /*Free Bets*/
  private readonly fBS = computed<FreeBet[]>(() => this.acS.fB$().filter(it => it.active));
  /*Free Bet*/
  private readonly fB = computed<FreeBet | undefined>(() => this.fBS()[0]);
  /*Tournament*/
  private readonly to = computed<boolean>(() => {
    const tournament = this.acS.t$();

    if (!tournament || tournament.status !== TournamentStatus.A) {
      return false;
    }

    return !tournament.min_bet || Calculator.lte(tournament.min_bet, this.am());
  });

  private step = 0;

  constructor() {
    super();

    effect(() => {
      this.acS.ba$();

      if (this.fB()) {
        return;
      }

      untracked(() => this.onAmountChange(this.currA, false));
    });

    effect(() => {
      if (this.acS.aR$()) {
        return;
      }

      untracked(() => this.onAmountChange(this.currA, false));
    });

    effect(() => {
      const fBet = this.fB();
      if (!fBet) {
        return;
      }

      if (this.ac()) {
        return;
      }

      if (!fBet.available_rounds) {
        this.rFB(fBet.id);
      }

      this.am.set(fBet.amount);
    });
  }

  public ngOnInit(): void {
    this.onAmountChange(this.c_a_m[this.code] || this.defB, false);

    this.acS.bE$.pipe(untilDestroyed(this)).subscribe(event => {
      if (!this.wR() && !this.aR()) {
        this.pending = event;
      } else {
        this.event(event);
      }
    });

    this.acS.tE$.pipe(untilDestroyed(this)).subscribe(event => {
      if (!this.acS.wP$()) {
        this.onStart(() => this.onTile(event.index));
      } else {
        this.onTile(event.index);
      }
    });
  }

  public onAmountChange(amount: string, alert: boolean = true): void {
    const minAmount = this.minA;
    const maxAmount = this.maxA;
    const balance = this.acS.ba$();

    let value = amount;
    if (Calculator.lt(balance, minAmount)) {
      value = this.aR() ? this.currA : minAmount;
      alert && this.low();
    } else if (Calculator.lt(amount, minAmount)) {
      value = minAmount;
      alert && this.min();
    } else if (Calculator.gt(amount, maxAmount)) {
      value = maxAmount;
      alert && this.max();
    } else if (Calculator.gt(amount, balance)) {
      value = this.aR() ? this.currA : balance;
      alert && this.low();
    }

    alert && this.sT(value);

    value = this.crS.round({ value });
    this.c_a_m = value;
    this.am.set(value);
  }

  public onSpace(): void {
    this.anS.client("bet_space");

    if (this.sTimeout) {
      clearTimeout(this.sTimeout);
    }

    this.sTimeout = setTimeout(() => {
      if (this.bDi()) {
        return;
      }

      if (this.st() === ButtonState.S) {
        this.onStart();
      } else {
        this.onAutopick();
      }
    }, Utils.second / 10);
  }

  public onAmountClick(event: Event): void {
    event.stopPropagation();

    this.anS.client("calculator_modal_open");

    const instance = this.mS.open({
      component: CalculatorModalComponent,
      data: { min: this.minA, max: this.maxA },
      styleClass: "m-m-b",
      outside: true,
    })!;

    instance.close$.pipe(take(1), filter(Boolean)).subscribe(() => {
      this.onAmountChange((instance.contentRef as ComponentRef<CalculatorModalComponent>).instance.amount);
    });
  }

  public onStart(fallback?: () => void): void {
    const amount = this.am();
    const balance = this.acS.ba$();
    const minAmount = this.minA;

    if (Calculator.lt(balance, minAmount) || Calculator.lt(balance, amount) || Calculator.lt(amount, minAmount)) {
      this.onAmountChange(amount, true);
      return;
    }

    this.acS.wP$.set(true);

    if (!this.fB()?.id) {
      this.acS.oE$.next({ type: OperationType.M, value: this.crS.round({ value: amount }) });
    }

    fallback && fallback();
  }

  public onAutopick(): void {
    const availableCells = this.acS.cells$().filter(cell => cell.state === "idle");

    if (!availableCells.length) {
      return;
    }

    this.onTile(availableCells[Math.floor(Math.random() * availableCells.length)].index);
  }

  public onTile(index: number): void {
    const start = this.step === 0;
    this.execute(
      () => {
        this.acS.aP$.set(true);

        return {
          step: this.step,
          bet: this.am(),
          client_seed: this.acS.cS$(),
          action: start ? "START" : "REVEAL",
          params: { mines_count: this.acS.mc$()!.mines_count, cells: [index] },
        };
      },
      round_id => {
        if (!start) {
          return;
        }

        this.acS.wR$.set(round_id);
        this.acS.cS$.set(Utils.seed());
      },
    );
  }

  public onCancel(): void {
    this.acS.wP$.set(false);

    this.acS.oE$.next({ type: OperationType.P, value: this.crS.round({ value: this.am() }) });
  }

  public onCashOut(): void {
    this.execute(() => {
      this.acS.aP$.set(true);

      return {
        bet: this.am(),
        action: "COLLECT",
        step: this.step,
        client_seed: this.acS.cS$(),
      };
    });
  }

  private execute(payload: () => RoundPlay, next?: (round_id: string) => void): void {
    if (this.ac()) {
      return;
    }

    this.acS.lR$.set(true);

    const body = payload();

    this.gS
      .play(body)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: round_id => {
          next?.(round_id);

          this.process(round_id);
        },
        error: (err: HttpError) => {
          this.reset(true);

          if ([HttpErrorCode.FB_N_A, HttpErrorCode.FB_N_F].includes(err?.error?.code)) {
            this.rFB(body.free_bet_id);
          }
        },
      });
  }

  private process(roundId: string): void {
    if (!this.pending || roundId !== this.pending.id) {
      return;
    }

    const pending = this.pending;
    this.pending = null;
    this.event(pending);
  }

  private event(event: BetEvent): void {
    if (event.type === BetEventType.S) {
      const activeRound = this.aR();

      if (event.id === this.wR() || event.id === activeRound?.id) {
        this.acS.aR$.set(event);
        this.acS.wR$.set("");

        if (event.finished) {
          if (event.data.selected_cells?.length === event.data.revealed_safe?.length) {
            /*todo*/
            //this.alS.win({ text: `${this.prefix}${this.crS.round({ value: event.total_win })}` });

            this.acS.oE$.next({ type: OperationType.P, value: event.total_win });
          }

          this.acS.oE$.next({ type: OperationType.U, value: "" });

          this.acS.rd$.update(rounds =>
            [
              {
                mines_count: event.data.mines_count,
                round_id: event.id,
                bet: event.total_bet,
                win: event.total_win,
                created_at: event.updated_at,
                multiplier: event.data.current_multiplier,
              },
              ...rounds,
            ].slice(0, Utils.limit),
          );

          if (event.is_max_win) {
            const instance = this.mS.open({ component: MaxWinModalComponent, styleClass: "m-m-w" })!;
            setTimeout(() => this.mS.close(instance), Utils.second * 3);
          }

          this.step = 0;
        } else {
          this.step += 1;
        }
      } else {
        this.reset(true);
      }
    } else {
      if ((event as CancelBetResponse).cancel_reason === RoundCancelReason.I_FU) {
        this.low();
      } else {
        this.error();
      }

      this.reset(true);
    }

    this.acS.lR$.set(false);
  }

  private reset(error: boolean): void {
    this.acS.wR$.set("");
    this.acS.wP$.set(false);
    this.acS.aP$.set(false);
    this.acS.lR$.set(false);

    if (error) {
      this.step = 0;
      this.acS.aR$.set(null);
    }
  }

  private get c_a_m(): Record<string, string> {
    return (this.clS.sF$()?.bet as Record<string, string>) || {};
  }

  private set c_a_m(value: string) {
    if (Calculator.eq(this.currA, value)) {
      return;
    }

    this.currA = value;
    this.clS.set("bet", { ...(this.c_a_m || {}), [this.code]: this.crS.round({ value }) });
  }

  private low(): void {
    const base = "error.low-balance";

    this.alS.show({ text: `${base}.title`, message: `${base}.msg`, badge: AlertBadge.E });
  }

  private error(): void {
    const base = "error.internal";

    this.alS.show({ text: `${base}.title`, message: `${base}.msg`, badge: AlertBadge.E });
  }

  private min(): void {
    this.alS.show({ text: "validation.bet.min", badge: AlertBadge.I });
  }

  private max(): void {
    this.alS.show({ text: "validation.bet.max", badge: AlertBadge.I });
  }

  private rFB(free_bet_id?: string): void {
    if (!free_bet_id) {
      return;
    }

    this.acS.fB$.update(freeBets => freeBets.filter(it => it.id !== free_bet_id));
  }

  private pFBC(free_bet_id?: string): void {
    if (!free_bet_id) {
      return;
    }

    const freeBet = untracked(this.fB);
    if (freeBet?.available_rounds) {
      freeBet.available_rounds += -1;
      this.acS.fB$.update(freeBets => freeBets.map(it => (it.id === freeBet.id ? freeBet : it)));
    }
  }

  private sT(amount: string): void {
    if (this.aS) {
      return;
    }

    const tournament = untracked(this.acS.t$);

    if (!tournament || tournament.status !== TournamentStatus.A || !tournament.min_bet) {
      return;
    }

    if (Calculator.gte(untracked(this.am), tournament.min_bet) && Calculator.lt(amount, tournament.min_bet)) {
      this.alS.show({ text: "tournament.condition", badge: AlertBadge.I });
      this.aS = true;
    }
  }
}
